import {
  Add as AddIcon,
  RequestQuote as BondsIcon,
  AccountBalance as BrokerIcon,
  CurrencyExchange as CashFlowsIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  PlayCircleOutline as DemoIcon,
  Receipt as ExpensesIcon,
  Download as ExportIcon,
  Group as GroupIcon,
  Upload as ImportIcon,
  TrendingUp as IncomeIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AccountBalance as PortfolioIcon,
  Person as ProfileIcon,
  Receipt as ReceiptIcon,
  Repeat as RecurringIcon,
  Replay as ReplayIcon,
  ShowChart as ShowChartIcon,
  TrendingUp,
} from "@mui/icons-material";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "./components/Auth/AuthContext";
import CurrencySwitcher from "./components/Auth/CurrencySwitcher";
import ContextToggle from "./components/Groups/contextToggle";
import { checkEmailVerified } from "./services/authService";
import { getPendingInvitations } from "./services/invitationService";

const useInvitationCount = (isLoggedIn, user) => {
  const [invitationCount, setInvitationCount] = useState(0);

  useEffect(() => {
    if (!isLoggedIn || !user) {
      setInvitationCount(0);
      return;
    }

    const fetchInvitationCount = async () => {
      try {
        if (!checkEmailVerified()) {
          console.log("Email not verified.");
          setInvitationCount(0);
          return;
        }
        if (!isLoggedIn || !user) {
          setInvitationCount(0);
          return;
        }
        const invitations = await getPendingInvitations();
        setInvitationCount(invitations.length);
      } catch (error) {
        if (error.response?.status === 401) {
          console.log("Unauthorized - email may not be verified");
          setInvitationCount(0);
          return;
        } else {
          console.error("Error fetching invitation count:", error);
        }
        setInvitationCount(0);
      }
    };

    const initialTimeout = setTimeout(() => {
      fetchInvitationCount();
    }, 1000);

    const interval = setInterval(fetchInvitationCount, 30000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isLoggedIn, user]);

  return invitationCount;
};

const appModeSelectSx = {
  "& .MuiOutlinedInput-root": {
    color: "white",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.5)" },
    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.8)" },
    "&.Mui-focused fieldset": { borderColor: "white" },
  },
  "& .MuiSvgIcon-root": { color: "white" },
};

const Navbar = () => {
  const { isLoggedIn, user, isDemoMode, exitDemoMode } =
    useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const invitationCount = useInvitationCount(isLoggedIn && !isDemoMode, user);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on navigation
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const portfolioRoutes = [
    "/portfolio",
    "/positions",
    "/transactions",
    "/bonds",
    "/cash-flows",
    "/brokers",
    "/stock-transactions",
    "/add-stock-transaction",
    "/import-portfolio",
    "/export-portfolio",
    "/stocks",
  ];

  // Neutral routes appear in both modes; don't switch the mode when visiting them
  const neutralRoutes = ["/profile"];
  const isNeutralRoute = neutralRoutes.some((r) =>
    location.pathname.startsWith(r),
  );

  const isPortfolioMode = isNeutralRoute
    ? localStorage.getItem("appMode") === "stocks"
    : portfolioRoutes.some((route) => location.pathname.startsWith(route));

  useEffect(() => {
    if (isLoggedIn && !isNeutralRoute) {
      localStorage.setItem("appMode", isPortfolioMode ? "stocks" : "budgeting");
    }
  }, [isPortfolioMode, isLoggedIn, isNeutralRoute]);

  const budgetingTabConfigs = [
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: <DashboardIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/expenses",
      label: "Expenses",
      icon: <ExpensesIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/income",
      label: "Income",
      icon: <IncomeIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/investments",
      label: "Investments",
      icon: <ShowChartIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/recurring-payments",
      label: "Recurring",
      icon: <RecurringIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/add-transaction",
      label: "Add Transaction",
      icon: <AddIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/categories",
      label: "Categories",
      icon: <CategoryIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/import-transactions",
      label: "Import",
      icon: <ImportIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/export-transactions",
      label: "Export",
      icon: <ExportIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/groups",
      label: "Groups",
      icon: <GroupIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/profile",
      label: "Profile",
      icon:
        invitationCount > 0 ? (
          <Badge
            badgeContent={invitationCount}
            color="error"
            sx={{ fontSize: 16 }}
          >
            <ProfileIcon sx={{ fontSize: 16 }} />
          </Badge>
        ) : (
          <ProfileIcon sx={{ fontSize: 16 }} />
        ),
    },
  ];

  const stocksTabConfigs = [
    {
      path: "/portfolio",
      label: "Portfolio",
      icon: <PortfolioIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/positions",
      label: "Positions",
      icon: <TrendingUp sx={{ fontSize: 16 }} />,
    },
    {
      path: "/brokers",
      label: "Brokers",
      icon: <BrokerIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/stock-transactions",
      label: "Activity",
      icon: <ReceiptIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/add-stock-transaction",
      label: "Add Order",
      icon: <AddIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/import-portfolio",
      label: "Import",
      icon: <ImportIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/export-portfolio",
      label: "Export",
      icon: <ExportIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/bonds",
      label: "Bonds",
      icon: <BondsIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/cash-flows",
      label: "Cash Flows",
      icon: <CashFlowsIcon sx={{ fontSize: 16 }} />,
    },
    {
      path: "/profile",
      label: "Profile",
      icon:
        invitationCount > 0 ? (
          <Badge
            badgeContent={invitationCount}
            color="error"
            sx={{ fontSize: 16 }}
          >
            <ProfileIcon sx={{ fontSize: 16 }} />
          </Badge>
        ) : (
          <ProfileIcon sx={{ fontSize: 16 }} />
        ),
    },
  ];

  const guestTabConfigs = [
    { path: "/register", label: "Register" },
    { path: "/login", label: "Login" },
  ];

  const handleExitDemo = () => {
    exitDemoMode();
    navigate("/login");
  };

  const handleReplayTour = () => {
    window.dispatchEvent(new Event("demo:replayTour"));
  };

  const hiddenInDemo = [
    "/groups",
    "/import-transactions",
    "/export-transactions",
    "/import-portfolio",
    "/export-portfolio",
  ];

  const rawTabConfigs = isLoggedIn
    ? isPortfolioMode
      ? stocksTabConfigs
      : budgetingTabConfigs
    : guestTabConfigs;

  const tabConfigs = isDemoMode
    ? rawTabConfigs.filter((c) => !hiddenInDemo.includes(c.path))
    : rawTabConfigs;
  const tabs = tabConfigs.map((config) => config.path);

  const selectedTab = tabs.includes(location.pathname)
    ? location.pathname
    : false;

  const handleModeChange = (event) => {
    const newMode = event.target.value;
    window.location.href = newMode === "stocks" ? "/portfolio" : "/dashboard";
  };

  return (
    <>
      {isDemoMode && (
        <Box
          sx={{
            background: "linear-gradient(135deg, #e65100 0%, #7b1fa2 100%)",
            color: "white",
            textAlign: "center",
            py: 0.6,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 2 },
            fontSize: "0.8rem",
            fontWeight: 500,
          }}
        >
          <DemoIcon sx={{ fontSize: 16 }} />
          <span>DEMO MODE — exploring with sample data.</span>
          <Tooltip title="Replay guided tour">
            <Chip
              label="Tour"
              icon={<ReplayIcon sx={{ fontSize: 12 }} />}
              size="small"
              onClick={handleReplayTour}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.6)",
                cursor: "pointer",
                height: 20,
                fontSize: "0.7rem",
                "& .MuiChip-icon": { color: "white" },
              }}
              variant="outlined"
            />
          </Tooltip>
          <Chip
            label="Exit Demo"
            size="small"
            onClick={handleExitDemo}
            sx={{
              color: "white",
              borderColor: "rgba(255,255,255,0.6)",
              cursor: "pointer",
              height: 20,
              fontSize: "0.7rem",
            }}
            variant="outlined"
          />
        </Box>
      )}
      <AppBar
        position="static"
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          height: "56px",
          minHeight: "56px",
        }}
      >
        <Toolbar
          sx={{ minHeight: "56px !important", height: "56px", px: 2, py: 0 }}
        >
          {isLoggedIn ? (
            <>
              {/* ══════════════════════════════════════════
                  DESKTOP (md = 900px and up)
                  ══════════════════════════════════════════ */}
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Tooltip title={isDemoMode ? "Click to replay tour" : ""} disableHoverListener={!isDemoMode}>
                  <Typography
                    variant="h6"
                    onClick={isDemoMode ? handleReplayTour : undefined}
                    sx={{
                      mr: 2, fontWeight: 600, color: "white",
                      ...(isDemoMode && {
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8, textDecoration: "underline" },
                      }),
                    }}
                  >
                    OrganizeFlow
                  </Typography>
                </Tooltip>

                <FormControl
                  size="small"
                  sx={{ mr: 2, minWidth: 140, ...appModeSelectSx }}
                  id="app-mode-select-control"
                >
                  <Select
                    id="app-mode-select"
                    value={isPortfolioMode ? "stocks" : "budgeting"}
                    onChange={handleModeChange}
                    sx={{ height: "36px", fontSize: "0.875rem", fontWeight: 500 }}
                  >
                    <MenuItem value="budgeting">💰 Budgeting</MenuItem>
                    <MenuItem value="stocks">📈 Stocks</MenuItem>
                  </Select>
                </FormControl>

                <Tabs
                  value={selectedTab}
                  onChange={() => {}}
                  textColor="inherit"
                  indicatorColor="secondary"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    flexGrow: 1,
                    "& .MuiTab-root": {
                      minHeight: "48px",
                      minWidth: "auto",
                      px: 1.5,
                      py: 0.5,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textTransform: "none",
                      color: "rgba(255, 255, 255, 0.8)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        color: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: 1,
                      },
                      "&.Mui-selected": {
                        color: "white",
                        fontWeight: 600,
                        backgroundColor: "rgba(255, 255, 255, 0.15)",
                        borderRadius: 1,
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#ffeb3b",
                      height: 3,
                      borderRadius: "2px 2px 0 0",
                    },
                    "& .MuiTabs-scrollButtons": {
                      color: "white",
                      "&.Mui-disabled": { opacity: 0.3 },
                    },
                  }}
                >
                  {tabConfigs.map((config) => (
                    <Tab
                      key={config.path}
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          {config.icon}
                          <span>{config.label}</span>
                        </Box>
                      }
                      component={Link}
                      to={config.path}
                      value={config.path}
                    />
                  ))}
                </Tabs>

                <Box sx={{ mx: 2 }}>
                  {isPortfolioMode ? <CurrencySwitcher /> : <ContextToggle />}
                </Box>

                {isDemoMode ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExitDemo}
                    sx={{
                      color: "white",
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      textTransform: "none",
                      fontWeight: 500,
                      px: 2,
                      py: 0.5,
                      minWidth: "auto",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    Exit Demo
                  </Button>
                ) : (
                  <Button
                    component={Link}
                    to="/logout"
                    variant="outlined"
                    size="small"
                    sx={{
                      color: "white",
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      textTransform: "none",
                      fontWeight: 500,
                      px: 2,
                      py: 0.5,
                      minWidth: "auto",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  >
                    Logout
                  </Button>
                )}
              </Box>

              {/* ══════════════════════════════════════════
                  MOBILE (below md)
                  ══════════════════════════════════════════ */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Tooltip title={isDemoMode ? "Click to replay tour" : ""} disableHoverListener={!isDemoMode}>
                  <Typography
                    variant="h6"
                    onClick={isDemoMode ? handleReplayTour : undefined}
                    sx={{
                      fontWeight: 600, color: "white", flexGrow: 1,
                      ...(isDemoMode && {
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 },
                      }),
                    }}
                  >
                    OrganizeFlow
                  </Typography>
                </Tooltip>

                <IconButton
                  color="inherit"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ color: "white" }}
                >
                  {invitationCount > 0 ? (
                    <Badge badgeContent={invitationCount} color="error">
                      <MenuIcon />
                    </Badge>
                  ) : (
                    <MenuIcon />
                  )}
                </IconButton>
              </Box>

              {/* ─── Drawer ─── */}
              <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: 260 } }}
              >
                <Box
                  sx={{
                    p: 2,
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  <FormControl size="small" sx={appModeSelectSx}>
                    <Select
                      value={isPortfolioMode ? "stocks" : "budgeting"}
                      onChange={handleModeChange}
                      sx={{ height: "36px", fontSize: "0.875rem", fontWeight: 500 }}
                    >
                      <MenuItem value="budgeting">💰 Budgeting</MenuItem>
                      <MenuItem value="stocks">📈 Stocks</MenuItem>
                    </Select>
                  </FormControl>

                  {isPortfolioMode ? <CurrencySwitcher /> : <ContextToggle />}

                  {isDemoMode && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "rgba(255,255,255,0.9)",
                        fontSize: "0.75rem",
                      }}
                    >
                      <DemoIcon sx={{ fontSize: 14 }} />
                      <span>Demo Mode</span>
                      <Chip
                        label="Tour"
                        icon={<ReplayIcon sx={{ fontSize: 10 }} />}
                        size="small"
                        onClick={() => {
                          setDrawerOpen(false);
                          handleReplayTour();
                        }}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.6)",
                          height: 18,
                          fontSize: "0.65rem",
                          "& .MuiChip-icon": { color: "white" },
                        }}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>

                <Divider />

                <List sx={{ flexGrow: 1 }}>
                  {tabConfigs.map((config) => (
                    <ListItem key={config.path} disablePadding>
                      <ListItemButton
                        component={Link}
                        to={config.path}
                        selected={location.pathname === config.path}
                        onClick={() => setDrawerOpen(false)}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "rgba(25, 118, 210, 0.1)",
                            "& .MuiListItemIcon-root": { color: "#1976d2" },
                            "& .MuiListItemText-primary": {
                              color: "#1976d2",
                              fontWeight: 600,
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                          {config.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={config.label}
                          primaryTypographyProps={{ fontSize: "0.9rem" }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>

                <Divider />

                <List>
                  <ListItem disablePadding>
                    {isDemoMode ? (
                      <ListItemButton onClick={handleExitDemo}>
                        <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                          <LogoutIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Exit Demo"
                          primaryTypographyProps={{ fontSize: "0.9rem" }}
                        />
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        component={Link}
                        to="/logout"
                        onClick={() => setDrawerOpen(false)}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                          <LogoutIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Logout"
                          primaryTypographyProps={{ fontSize: "0.9rem" }}
                        />
                      </ListItemButton>
                    )}
                  </ListItem>
                </List>
              </Drawer>
            </>
          ) : (
            <>
              <Typography
                variant="h6"
                sx={{ mr: 3, fontWeight: 600, color: "white", flexGrow: 1 }}
              >
                OrganizeFlow
              </Typography>

              <Tabs
                value={selectedTab}
                onChange={() => {}}
                textColor="inherit"
                indicatorColor="secondary"
                sx={{
                  "& .MuiTab-root": {
                    minHeight: "48px",
                    px: 3,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    textTransform: "none",
                    color: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      color: "white",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: 1,
                    },
                    "&.Mui-selected": {
                      color: "white",
                      fontWeight: 600,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#ffeb3b",
                    height: 3,
                    borderRadius: "2px 2px 0 0",
                  },
                }}
              >
                {guestTabConfigs.map((config) => (
                  <Tab
                    key={config.path}
                    label={config.label}
                    component={Link}
                    to={config.path}
                    value={config.path}
                  />
                ))}
              </Tabs>
            </>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Navbar;
